(() => {
  'use strict';
  const normalize = value => String(value ?? '').normalize('NFKC').trim().toUpperCase();
  const letterSize = value => {
    const text=normalize(value);
    const pattern='XXXS|XXS|XS|S|M/L|ML|M|L|XXXL|XXL|XL';
    const exact=text.match(new RegExp('^('+pattern+')$'));
    const leading=text.match(new RegExp('^('+pattern+')(?=\\s|[—–-])'));
    const bracket=text.match(new RegExp('\\(('+pattern+')\\)(?=$|\\s*[—–-])'));
    const wheel=text.match(new RegExp('^\\d+(?:\\.\\d+)?["″]\\s+('+pattern+')(?=,|$)'));
    const size=(exact||bracket||wheel||leading)?.[1];return size==='ML'?'M/L':size||null;
  };
  const validRow = row => row && typeof row.size === 'string' && Number.isFinite(row.min) && Number.isFinite(row.max) && row.min >= 50 && row.max <= 250 && row.min <= row.max;
  const matchingRows = (height, rows) => Number.isFinite(height) && height >= 50 && height <= 250 ? (Array.isArray(rows) ? rows : []).filter(row => validRow(row) && row.min <= height && height <= row.max) : [];
  const matchesSize = (variant, size) => {
    if (size === 'One size') return true;
    const wanted = normalize(size)==='ML'?'M/L':normalize(size);
    return (variant.options || []).some(option => {
      const text = normalize(typeof option === 'object' ? option.value : option);
      return text === wanted || letterSize(text) === wanted;
    });
  };
  const matchesRow = (variant, row) => Array.isArray(row.variantIds) ? row.variantIds.includes(String(variant.id).split('/').pop()) : matchesSize(variant, row.size);
  const matchingVariants = (variants, rows) => (variants || []).filter(variant => variant.available === true && rows.some(row => matchesRow(variant, row)));
  const toCm = (unit, cm, feet, inches) => {
    if (unit === 'cm') return String(cm).trim() ? Number(cm) : NaN;
    if (!String(feet).trim() || !String(inches).trim()) return NaN;
    const f = Number(feet), i = Number(inches);
    if (!Number.isInteger(f) || f < 1 || f > 8 || !Number.isFinite(i) || i < 0 || i >= 12) return NaN;
    return Math.round((f * 12 + i) * 254) / 100;
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = {matchingRows, matchesSize, matchesRow, matchingVariants, toCm, validRow, letterSize};
  if (typeof document === 'undefined') return;
  const catalogueCache = new Map();
  const requestJson = async url => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, {signal: controller.signal, headers: {Accept:'application/json'}});
      if (!response.ok) throw new Error('Unavailable');
      return await response.json();
    } finally {clearTimeout(timeout);}
  };
  const cachedJson = (cache, url) => {
    if (!cache.has(url)) cache.set(url, requestJson(url).catch(error => {cache.delete(url); throw error;}));
    return cache.get(url);
  };
  const node = (tag, text, className) => {
    const element = document.createElement(tag);
    if (text !== undefined) element.textContent = text;
    if (className) element.className = className;
    return element;
  };
  function init(root) {
    if (root.dataset.fitInitialized) return;
    root.dataset.fitInitialized = 'true';
    const get = selector => root.querySelector(selector);
    let profile, product, strings;
    try {
      profile = JSON.parse(get('[data-fit-profile]').textContent);
      product = JSON.parse(get('[data-fit-product]').textContent);
      strings = JSON.parse(get('[data-fit-strings]').textContent);
    } catch {return;}
    const t = (key, vars = {}) => Object.entries(vars).reduce((text,[name,value]) => text.replaceAll('{' + name + '}',String(value)), strings[key] || key);
    const rows = (profile.rows || []).filter(validRow);
    const chart = get('[data-fit-chart]');
    chart.replaceChildren();
    if (rows.length) {
      const table = node('table');
      const caption = node('caption',t('table_caption')); caption.className = 'lifty-fit__fine'; table.append(caption);
      const head = node('thead'), heading = node('tr');
      for (const label of [t('size'),t('rider_height')]) {const th = node('th',label); th.scope='col'; heading.append(th);}
      head.append(heading); table.append(head);
      const body = node('tbody');
      for (const row of rows) {
        const tr = node('tr'); tr.dataset.size = row.size;
        tr.append(node('td',row.size === 'One size' ? t('one_size') : row.size),node('td',`${row.min}–${row.max} cm`));
        body.append(tr);
      }
      table.append(body); chart.append(table);
      if (typeof profile.source === 'string' && profile.source.startsWith('https://')) {
        const source = get('[data-fit-source]'); source.href = profile.source; source.hidden = false;
      }
      get('[data-fit-date]').textContent = t('checked',{date:profile.checked});
    } else chart.append(node('p',root.dataset.kind === 'euc' ? t('unknown_euc') : t('unknown')));
    if (Array.isArray(profile.facts) && profile.facts.length) {
      const facts=node('ul');
      for(const fact of profile.facts) if(typeof fact==='string')facts.append(node('li',fact));
      chart.append(facts);
    }
    if (typeof profile.source === 'string' && profile.source.startsWith('https://')) {
      const source=get('[data-fit-source]');source.href=profile.source;source.hidden=false;
      get('[data-fit-date]').textContent=t('checked',{date:profile.checked});
    }
    get('[data-fit-note]').textContent = profile.note || '';
    const units = get('[data-fit-units]'), heightInput = get('[data-fit-height]'), feet = get('[data-fit-feet]'), inches = get('[data-fit-inches]');
    const result = get('[data-fit-result]'), recs = get('[data-fit-recommendations]'), status = get('[data-fit-rec-status]'), cards = get('[data-fit-cards]');
    const rootUrl = (root.dataset.root || '/').replace(/\/?$/, '/');
    const productUrl = handle => rootUrl + 'products/' + encodeURIComponent(handle);
    const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-IE',{style:'currency',currency:root.dataset.currency || 'EUR'}).format(cents/100);
    const browse=get('[data-fit-size-browse]');
    if(browse) {
      const select=get('[data-fit-size-label]'),browseStatus=get('[data-fit-size-status]'),browseCards=get('[data-fit-size-cards]'),more=get('[data-fit-size-more]');
      let browseGeneration=0,browseCandidates=[],browseCursor=0,browseCount=0;
      async function loadSizes(fresh) {
        const token=fresh?++browseGeneration:browseGeneration,label=select.value;
        if(fresh){browseCards.replaceChildren();browseCount=0;browseCursor=0;browseCandidates=[];more.hidden=true;}
        if(!label){browseStatus.textContent=t('size_browse_start');return;}
        more.disabled=true;browseStatus.textContent=t('size_browse_loading',{size:label});
        try {
          if(fresh){const all=await cachedJson(catalogueCache,root.dataset.sizeCatalogueUrl);if(token!==browseGeneration)return;browseCandidates=all.filter(p=>p.sizes.includes(label));}
          const page=browseCandidates.slice(browseCursor,browseCursor+12);browseCursor+=page.length;
          const results=await Promise.all(page.map(async candidate=>{
            try {
              const live=await requestJson(productUrl(candidate.handle)+'.js');
              const options=(live.variants||[]).filter(v=>v.available===true && matchesSize(v,label));
              if(!options.length)return null;
              options.sort((a,b)=>a.price-b.price);return {candidate,live,variant:options[0]};
            }catch{return null;}
          }));
          if(token!==browseGeneration||!root.isConnected)return;
          for(const entry of results.filter(Boolean)) {
            const {candidate,live,variant}=entry,card=node('a',undefined,'lifty-fit__card');card.href=productUrl(candidate.handle)+'?variant='+encodeURIComponent(variant.id);
            const optionLabel=variant.title.replace(/\s*[—–-]\s*\d{2,3}(?:\.\d+)?\s*[–-]\s*\d{2,3}(?:\.\d+)?\s*cm/gi,'');
            card.append(node('strong',live.title),node('span',optionLabel));
            const guide=(candidate.profile?.note?[]:candidate.profile?.rows||[]).filter(row=>validRow(row)&&matchesRow(variant,row));
            if(guide.length)card.append(node('span',guide.map(row=>`${row.min}–${row.max} cm`).join(' / ')));
            card.append(node('span',money(variant.price),'lifty-fit__price'),node('span',t('orderable')),node('span',t('view_option'),'lifty-fit__action'));browseCards.append(card);browseCount++;
          }
          browseStatus.textContent=t('size_browse_count',{count:browseCount,size:label,checked:browseCursor,total:browseCandidates.length});
          more.hidden=browseCursor>=browseCandidates.length;more.disabled=false;
        }catch{if(token===browseGeneration){browseStatus.textContent=t('rec_error');more.hidden=true;}}
      }
      select.addEventListener('change',()=>loadSizes(true));more.addEventListener('click',()=>loadSizes(false));
    }
    let generation = 0;
    const contact = height => {
      const body = `${product.title}\n${location.origin}${productUrl(product.handle)}\n${t('email_height')}: ${Number.isFinite(height) ? Number(height.toFixed(2)) + ' cm' : ''}\n${t('email_inseam')}: \n${t('email_request')}`;
      get('[data-fit-contact]').href = 'mailto:hello@lifty.co?subject=' + encodeURIComponent(t('email_subject')+' — '+product.title) + '&body='+encodeURIComponent(body);
    };
    contact(NaN);
    const reset = () => {generation++; result.replaceChildren(); recs.hidden=true; cards.replaceChildren(); root.querySelectorAll('tr[data-size]').forEach(row=>delete row.dataset.match); contact(NaN);};
    units.addEventListener('change',()=>{
      const metric = units.value === 'cm';
      get('[data-fit-metric]').hidden = !metric; get('[data-fit-imperial]').hidden=metric;
      heightInput.disabled=!metric; heightInput.required=metric;
      for (const input of [feet,inches]) {input.disabled=metric; input.required=!metric;}
      reset();
    });
    for (const input of [heightInput,feet,inches]) input.addEventListener('input',reset);
    async function recommendations(height, token) {
      recs.hidden=false; cards.replaceChildren(); status.textContent=t('loading');
      try {
        const catalogue = await cachedJson(catalogueCache,root.dataset.catalogueUrl);
        if (generation !== token) return;
        const candidates = catalogue.filter(p=>p.handle!==product.handle && p.kind===root.dataset.kind && !p.profile.note && matchingRows(height,p.profile.rows).length);
        const matches = []; let cursor=0, failed=0;
        async function worker() {
          while (cursor < candidates.length && generation===token) {
            const candidate=candidates[cursor++];
            try {
              const live=await requestJson(productUrl(candidate.handle)+'.js');
              const fitting=matchingRows(height,candidate.profile.rows);
              const available=matchingVariants(live.variants,fitting);
              if (available.length) {
                available.sort((a,b)=>a.price-b.price);
                matches.push({candidate,live,variant:available[0],fitting});
              }
            } catch {failed++;}
          }
        }
        await Promise.all(Array.from({length:Math.min(4,candidates.length)},worker));
        if (generation !== token || !root.isConnected) return;
        matches.sort((a,b)=>a.variant.price-b.variant.price || a.live.title.localeCompare(b.live.title));
        for (const {candidate,live,variant,fitting} of matches.slice(0,8)) {
          const card=node('a',undefined,'lifty-fit__card');card.href=productUrl(candidate.handle)+'?variant='+encodeURIComponent(variant.id);
          const src=live.featured_image;
          if (typeof src==='string' && /^(https:\/\/|\/\/)/.test(src)) {const img=node('img');img.src=src;img.alt=live.title;img.loading='lazy';img.width=240;img.height=180;card.append(img);}
          card.append(node('strong',live.title));
          const matching=fitting.filter(row=>matchesRow(variant,row));
          card.append(node('span',matching.map(row=>(row.size==='One size'?t('one_size'):row.size)+`: ${row.min}–${row.max} cm`).join(' / ')));
          if (variant.title && variant.title!=='Default Title') card.append(node('span',variant.title));
          card.append(node('span',money(variant.price),'lifty-fit__price'),node('span',t('orderable')),node('span',t('view_option'),'lifty-fit__action'));
          cards.append(card);
        }
        status.textContent=matches.length?t('rec_found',{height:Number(height.toFixed(2)),count:Math.min(8,matches.length)}):t('rec_none');
        if (failed) status.append(' '+t('partial'));
      } catch {if(generation===token) status.textContent=t('rec_error');}
    }
    get('[data-fit-form]').addEventListener('submit',event=>{
      event.preventDefault();
      const height=toCm(units.value,heightInput.value,feet.value,inches.value);
      if(!Number.isFinite(height)||height<50||height>250) {result.replaceChildren(node('strong',t('invalid')));return;}
      const token=++generation;
      result.replaceChildren();contact(height);
      const matches=matchingRows(height,rows);
      root.querySelectorAll('tr[data-size]').forEach(row=>row.dataset.match=String(matches.some(match=>match.size===row.dataset.size)));
      if(!rows.length) {
        result.append(node('strong',t('needs_check')),node('p',root.dataset.kind==='euc'?t('unknown_euc'):t('unknown')));
      } else if(!matches.length) {
        result.append(node('strong',t('outside')),node('p',t('outside_body',{height:Number(height.toFixed(2))})));
      } else {
        result.append(node('strong',profile.note?t('revision_check'):t('within')),node('p',t('within_body',{height:Number(height.toFixed(2)),sizes:matches.map(row=>row.size==='One size'?t('one_size'):row.size).join(' / ')})));
        const available=matchingVariants(product.variants,matches);
        if(profile.note) result.append(node('p',t('revision_body')));
        else if(available.length) {
          result.append(node('p',t('matching_options')));
          const list=node('ul');
          for(const variant of available) {const li=node('li');const link=node('a',variant.title==='Default Title'?t('view_option'):variant.title);link.href=productUrl(product.handle)+'?variant='+encodeURIComponent(variant.id);li.append(link);list.append(li);}
          result.append(list);
        } else result.append(node('p',t('size_unavailable')));
      }
      result.append(node('p',t('fit_note')));
      recommendations(height,token);
    });
  }
  const start = scope => scope.querySelectorAll('[data-lifty-fit]').forEach(init);
  start(document);
  document.addEventListener('shopify:section:load',event=>start(event.target));
})();
